import type { ReactNode } from "react";
import { clamp, cx } from "./utils";
import type { Tone } from "./Badge";

export interface ProgressRingProps {
  /** Progress in 0–1. */
  value: number;
  /** Outer diameter in px. @default 64 */
  size?: number;
  /** Stroke width in px. @default 6 */
  thickness?: number;
  /** Accessible name, e.g. "今日总进度". */
  label: string;
  /** Text drawn in the middle; defaults to a rounded percentage. */
  children?: ReactNode;
  /** @default "info" */
  tone?: Tone;
  /** Hide the centre text entirely. */
  bare?: boolean;
  className?: string;
}

/** SVG progress ring (task completion, 22 s recording limit). */
export function ProgressRing({
  value,
  size = 64,
  thickness = 6,
  label,
  children,
  tone = "info",
  bare = false,
  className,
}: ProgressRingProps) {
  const v = clamp(value, 0, 1);
  const r = (size - thickness) / 2;
  const c = 2 * Math.PI * r;
  return (
    <div
      className={cx("xz-ring", `xz-ring--${tone}`, className)}
      role="progressbar"
      aria-label={label}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(v * 100)}
      style={{ width: size, height: size }}
    >
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden="true">
        <circle
          className="xz-ring__track"
          cx={size / 2}
          cy={size / 2}
          r={r}
          strokeWidth={thickness}
          fill="none"
        />
        <circle
          className="xz-ring__bar"
          cx={size / 2}
          cy={size / 2}
          r={r}
          strokeWidth={thickness}
          fill="none"
          strokeLinecap="round"
          strokeDasharray={c}
          strokeDashoffset={c * (1 - v)}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
        />
      </svg>
      {!bare && <span className="xz-ring__text">{children ?? `${Math.round(v * 100)}%`}</span>}
    </div>
  );
}

export interface ProgressBarProps {
  /** Progress in 0–1. */
  value: number;
  /** Accessible name. */
  label: string;
  /** Show the label and percentage above the bar. */
  showLabel?: boolean;
  /** @default "info" */
  tone?: Tone;
  /** @default "md" */
  size?: "sm" | "md";
  className?: string;
}

/** Design-system progress bar — a native `<progress class="gz-progress">`. */
export function ProgressBar({
  value,
  label,
  showLabel = false,
  tone = "info",
  size = "md",
  className,
}: ProgressBarProps) {
  const v = clamp(value, 0, 1);
  return (
    <div className={cx("xz-progress", `xz-progress--${tone}`, `xz-progress--${size}`, className)}>
      {showLabel && (
        <div className="xz-progress__head">
          <span>{label}</span>
          <span>{Math.round(v * 100)}%</span>
        </div>
      )}
      <progress className="gz-progress" max={100} value={Math.round(v * 100)} aria-label={label}>
        {Math.round(v * 100)}%
      </progress>
    </div>
  );
}
